import * as React from "react";
import {createElement as h} from "react";
import {cx} from "emotion";
import {mix} from "polished";

import "./webchat.scss";

import {XdSentry}       from "@Common/libs/sentry/xd-sentry";
import {bindInput}      from "@Common/react/bind-input";
import {cs}             from "@Common/react/chain-services";
import {consumeContext} from "@Common/react/context";
import {Link}           from "@Common/react/cst2";
import {Invoke}         from "@Common/react/invoke";
import {scopedState}    from "@Common/react/scoped-state";
import {UseState}       from "@Common/react/use-state";
import {Wait}           from "@Common/react/wait";
import {isBlankStr}     from "@Common/utils/strings";

import {WebchatIntegration} from "./webchat-integration";

export const Webchat = ({next}:{
    next    : Link<React.ReactNode, {
        show        : () => void,
        showing     : boolean,
        minimized   : boolean,
        setOrderId  : (newOrderId:number) => void,
    }>,
}) => cs(
    ["showChatWindow", (_, next) => UseState({
        next,
    })],
    ["minimized", (_, next) => UseState({
        initValue: false,
        next,
    })],
    ["orderId", (_, next) => UseState({
        initValue: 0,
        next,
    })],
    consumeContext("liveChatConfig"),

    ({showChatWindow, minimized, orderId, liveChatConfig}) => <>
        {next({
            show: () => {
                minimized.onChange(false);
                showChatWindow.onChange(true);
            },
            showing: !minimized.value && showChatWindow.value,
            minimized: minimized.value,
            setOrderId: (newOrderId) => {
                if (newOrderId !== orderId.value) {
                    // Invoke fn doesn't fire from here, so using the timeout directly to apply the adjustment when called during the render loop
                    setTimeout(() => orderId.onChange(newOrderId), 0);
                }
            }
        })}

        {liveChatConfig?.chatEnabled && cs(
            ["webchat", (_, next) => h(WebchatIntegration, {
                config: liveChatConfig,
                containerEl: "chat-container",
                next,
            })],
            consumeContext("themeScheme"),
            consumeContext("apis"),

            // If autoJoined then show the Chat window automatically
            ({webchat}, next) => <>
                {next()}

                {showChatWindow.value === undefined && webchat.autoJoined && (
                    Invoke({
                        action: () => {
                            showChatWindow.onChange(true);
                            try {
                                XdSentry.addBreadcrumb({
                                    category: "chat",
                                    message: "ID: " + liveChatConfig.customField1 + " Order ID: " + liveChatConfig.customField2 + " Reconnected to chat from URL: " + window.location.href,
                                    level: XdSentry.Severity.Info,
                                    data: liveChatConfig
                                });
                                XdSentry.captureMessage("Xpresso chat reconnected.", XdSentry.Severity.Log);
                            } catch (error) {
                                console.log('There was an error attempting to log chat to sentry: ' + error.toString() );
                            }
                        },
                    })
                )}
            </>,
            ({webchat, themeScheme, apis}) => (
                <div
                    id={"web-chat-window-id"}
                    className={cx("web-chat-window-12dw", {show: showChatWindow.value, minimized: minimized.value})}
                >
                    <div className="chat-header" style={{background: mix(0.1, "white", themeScheme.xpresso.menuBackgroundColorShadow), color: themeScheme.xpresso.menuTextColor}}>
                        <div className="text">Live Chat</div>
                        <div className="buttons">
                            <a {...{
                                "xd-qa-type": "icon",
                                onClick: () => minimized.change((_) => !_),
                                ...minimized.value ? {
                                    children: "+",
                                    "xd-qa-name": "maximize",
                                } : {
                                    children: "-",
                                    "xd-qa-name": "minimize",
                                },
                            }}/>

                            <a
                                xd-qa-type="icon" xd-qa-name="close_chat"
                                onClick={() => {
                                    webchat.close();
                                    showChatWindow.onChange(false);
                                }}
                            >
                                &times;
                            </a>
                        </div>
                    </div>
                    <div className={cx("chat_body")} >
                        {(!webchat.started && !webchat.autoJoined) && (
                            !liveChatConfig.holidayMessage ? (
                                rCustomerInfoForm({
                                    firstName: liveChatConfig.first_name,
                                    lastName: liveChatConfig.last_name,
                                    onDone: async (customerInfo) => {

                                        let updatedChatConfig = await apis.next.getUpdatedLiveChatConfig(customerInfo.firstName, customerInfo.lastName);

                                        // abort if chat has been disabled or chat is now after hours
                                        if( !updatedChatConfig.chatEnabled || updatedChatConfig.holidayMessage ) {
                                            showChatWindow.onChange(false);
                                            alert(updatedChatConfig.holidayMessage || `Live chat has closed. Please try again Monday through Friday between ${updatedChatConfig.contactCenterStartFormatted} and ${updatedChatConfig.contactCenterEndFormatted} CST.`);
                                            return;
                                        }

                                        if( themeScheme.type === "loggedIn" )
                                        {
                                            // set the order ID if field 2 label used for an order ID
                                            if (updatedChatConfig?.customField2Label === "Order ID" && orderId.value) {
                                                updatedChatConfig.customField2 = orderId.value;
                                            }

                                            webchat.startChat(updatedChatConfig);
                                        }
                                        else
                                        {
                                            webchat.startChat({
                                                ...themeScheme.liveChatInfo,
                                                firstName: customerInfo.firstName,
                                                lastName: customerInfo.lastName
                                            });
                                        }
                                        try {
                                            XdSentry.addBreadcrumb({
                                                category: "chat",
                                                message: "ID: " + liveChatConfig.customField1 + " Order ID: " + liveChatConfig.customField2 + " Connected to chat from URL: " + window.location.href,
                                                level: XdSentry.Severity.Info,
                                                data: liveChatConfig
                                            });
                                            XdSentry.captureMessage("Xpresso chat connected.", XdSentry.Severity.Log);
                                        } catch (error) {
                                            console.log('There was an error attempting to log chat to sentry: ' + error.toString() );
                                        }
                                    },
                                })
                            ) : (
                                rOfflineMsg(liveChatConfig.holidayMessage)
                            )
                        )}

                        <div id="chat-container" style={{display: webchat.started || webchat.autoJoined ? "block" : "none"}}/>
                    </div>
                </div>
            )
        )}
    </>,
);

const rOfflineMsg = (message) => (
    <div className="offline_chat_msg">
        {message}
    </div>
);

const rCustomerInfoForm = ({firstName, lastName, onDone}) => cs(
    ["customerInfo", (_, next) => UseState({
        initValue: {
            firstName,
            lastName,
        },
        next,
    })],
    ({customerInfo}) => (
        <div className="customer-info">
            <h3 className="title">Please enter your information</h3>
            <table className="webchat-config">
                <tbody>
                <tr>
                    <th>First Name:</th>
                    <td>
                        <input
                            size={10}
                            xd-qa-type="text_field"
                            xd-qa-name="First Name"
                            {...bindInput(scopedState(customerInfo, ["firstName"]))}
                        />
                    </td>
                </tr>
                <tr>
                    <th>Last Name:</th>
                    <td>
                        <input
                            size={10}
                            xd-qa-type="text_field"
                            xd-qa-name="Last Name"
                            {...bindInput(scopedState(customerInfo, ["lastName"]))}
                        />
                    </td>
                </tr>
                </tbody>
            </table>

            {cs(
                ["wait", (_, next) => <Wait {...{
                    delay: 5000,
                    next
                }}/>],
                ({wait}) => !wait && (
                    <div className="load-msg">
                        Please wait a few seconds for live chat to load...
                    </div>
                )
            )}

            <div className="buttons">
                <button
                    xd-qa-type="button"
                    xd-qa-name="Start Chat"
                    onClick={(e) => {
                        e.preventDefault();
                        const {firstName, lastName} = customerInfo.value;
                        if (isBlankStr(firstName) || isBlankStr(lastName)) {
                            alert("Please enter your first and last name")
                        } else {
                            onDone(customerInfo.value);
                        }
                    }}
                >
                    Start Chat
                </button>
            </div>

        </div>
    )
);
